/**
 * NeedSeeAllSheet — "See all →" full, filterable category for a plan Need
 * (Smart Recommendations R1, frontend spec §7 task 4) — the chat-linear
 * realization of "tabs that expand".
 *
 * Reuses the SAME `@garden/ui` `BottomSheet` primitive `CitationChip` opens
 * (focus trap + Escape-to-close + focus restore + `role="dialog"` all come
 * from there — see `CitationChip.tsx`), and the EXISTING marketplace search
 * data layer (`useMarketplaceSearch`, STORY-003-008) + `ProductCard` /
 * `SortSelect` / `StockToggle` (STORY-003-005) — no new backend endpoint. The
 * Need's own `retrieval.query_text` (falling back to `label`) seeds the
 * search query; sort + in-stock are the SAME filters the `/marketplace/
 * search` results page exposes, so "filterable" is real, not decorative.
 */
import { BottomSheet, Skeleton } from "@garden/ui";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { ProductCard } from "@/features/marketplace/ProductCard";
import { SortSelect, type SortValue } from "@/features/marketplace/SortSelect";
import { StockToggle } from "@/features/marketplace/StockToggle";
import { useNeedProducts } from "@/features/chat/useNeedProducts";
import type { Need } from "@/features/chat/useChatStream";

export interface NeedSeeAllSheetProps {
  /** The Need to browse, or `null` when the sheet is closed. */
  need: Need | null;
  onClose: () => void;
  /** Opens as the desktop (right-side) variant. */
  desktop?: boolean;
}

export function NeedSeeAllSheet({
  need,
  onClose,
  desktop = false,
}: NeedSeeAllSheetProps): ReactElement {
  const { t, i18n } = useTranslation("chat");
  const [sort, setSort] = useState<SortValue>("relevance");
  const [inStockOnly, setInStockOnly] = useState(false);

  // SCRUM-187 — expand the Need through the SAME semantic retrieval the chip
  // used. This previously ran the LEXICAL marketplace search on the Need's
  // label, which cannot bridge languages: an English label found nothing in a
  // Russian catalogue even while the chip beside it showed four products.
  const { items, isLoading } = useNeedProducts({
    need,
    language: i18n.language,
    sort,
    inStockOnly,
  });

  return (
    <BottomSheet
      open={need !== null}
      onClose={onClose}
      title={need?.label ?? ""}
      desktop={desktop}
    >
      <div className="flex flex-col gap-3" data-testid="chat-need-see-all">
        <div className="flex items-center gap-3">
          <SortSelect value={sort} onChange={setSort} />
          <StockToggle checked={inStockOnly} onChange={setInStockOnly} />
        </div>

        {isLoading ? (
          <div
            className="flex flex-col gap-2"
            data-testid="chat-need-see-all-loading"
          >
            <Skeleton w="100%" h={72} />
            <Skeleton w="100%" h={72} />
            <Skeleton w="100%" h={72} />
          </div>
        ) : items.length === 0 ? (
          <p
            className="py-6 text-center text-[15px]/[1.429] text-muted-foreground"
            data-testid="chat-need-see-all-empty"
          >
            {t("plan.seeAllEmpty")}
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map((product, i) => (
              <ProductCard key={product.id} product={product} position={i} />
            ))}
          </div>
        )}

      </div>
    </BottomSheet>
  );
}

export default NeedSeeAllSheet;
